import { useEffect, useState, useRef, Fragment } from 'react';
import { subscriptionAPI } from '../../../../services/redwolfSdkAPI';
import * as Sentry from '@sentry/react';

import useTechnicalErrorMessage from '../../../common/hooks/useTechnicalErrorMessage';
import Pagination from '../../../common/components/Pagination';
import UpdateSubscription from './UpdateSubscription';
import RenewSubscription from './RenewSubscription';
import PurchaseHistory from './PurchaseHistory';
import Select from 'react-select';
import DropdownIndicator from '../../../common/components/DropdownIndicator';
import { useDispatch } from 'react-redux';
import lodash from 'lodash';
import { reactSelectStyles, isMobile } from '../../../../utils/constants';
import useRequestRedwolfService from '../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../utils/localStorage';
import TableSkeleton from '../../../common/skeletons/TableSkeleton';
import { openNotificationError } from '../../../../store/commonSlice';
import { notification } from '../../../../utils/constants';
import AppBundleUpdateSubscription from './AppBundleUpdateSubscription';
import AppBundleRenewSubscription from './AppBundleRenewSubscription';

const Subscriptions = () => {
	const [subscriptionsList, setSubscriptionsList] = useState<any>([]);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [plansList, setPlansList] = useState([]);
	const [isSkeletonLoading, setIsSkeletonLoading] = useState(false);
	const [isSubscriptionSkeleton, setIsSubscriptionSkeleton] = useState(false);
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [requestRedwolfService] = useRequestRedwolfService();
	const [currentPageNumber, setCurrentPageNumber] = useState(1);
	const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
	const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
	const [isUpdateAppBundleModalOpen, setIsUpdateAppBundleModalOpen] = useState(false);
	const [isRenewAppBundleModalOpen, setIsRenewAppBundleModalOpen] = useState(false);
	const [subscriptionDetail, setSubscriptionDetail] = useState<any>();
	const [updatePurchaseHistory, setUpdatePurchaseHistory] = useState(false);
	const subscriptionContainerFocusRef: any = useRef<HTMLDivElement>(null);
	const subscriptionTabindex = !isMobile ? { tabIndex: -1 } : {};

	const transaction = Sentry.startInactiveSpan({ name: 'Subscriptions' });

	const dispatch = useDispatch();

	const subscriptionDropDownOptions = [
		{
			label: 'Active',
			value: 'active'
		},
		{
			label: 'Canceled',
			value: 'canceled'
		}
	];
	const token = getToken();

	useEffect(() => {
		if (subscriptionContainerFocusRef.current) {
			subscriptionContainerFocusRef.current.focus();
		}
		getSubscriptionList();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onDropdownKeyDown = (event: any) => {
		if (event.code === 'Enter') {
			setIsDropdownOpen(!isDropdownOpen);
		}
	};

	const onDropdownMenuClose = () => {
		setIsDropdownOpen(false);
	};

	const onDropdownMenuOpen = () => {
		setIsDropdownOpen(true);
	};

	const sortSubscriptions = (subscriptions: any, sortBy: any) => {
		const sortedSubscriptions = lodash.orderBy(
			subscriptions,
			['status', 'plan.appName'],
			[sortBy, 'asc']
		);
		setSubscriptionsList(sortedSubscriptions);
	};

	const handleSorting = (event: any) => {
		if (event.value === 'active') {
			sortSubscriptions(subscriptionsList, 'asc');
		} else {
			sortSubscriptions(subscriptionsList, 'desc');
		}

		setCurrentPageNumber(1);
		setIsDropdownOpen(false);
	};

	const handleUpdatePurchaseHistory = () => {
		setUpdatePurchaseHistory(!updatePurchaseHistory);
	};

	const getSubscriptionList = () => {
		setSubscriptionsList([]);
		setIsSkeletonLoading(true);

		requestRedwolfService(subscriptionAPI.getSubscriptions(token))
			.then((response: any) => {
				setIsSkeletonLoading(false);
				if (
					response.constructor.$metadata$.simpleName === 'Success' &&
					response.data.toArray().length
				) {
					sortSubscriptions(response.data.toArray(), 'asc');
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSkeletonLoading(true);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const handleUpdateSubscriptionModal = (event: any, subscription: any, updateType: any) => {
		event.preventDefault();

		if (updateType === 'Renew') {
			setIsRenewModalOpen(true);
		} else {
			setIsUpdateModalOpen(true);
		}

		setIsSubscriptionSkeleton(true);

		requestRedwolfService(subscriptionAPI.getPlanUpgrades(token, subscription.plan.planCode))
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success') {
					setIsSubscriptionSkeleton(false);
					const fetchData = response.data.toArray();

					const preSelectedPlan = [
						{
							planCode: subscription.plan.planCode,
							appName: subscription.plan.appName,
							name: subscription.plan.name,
							price: subscription.plan.price,
							billingInterval: subscription.plan.billingInterval,
							billingDate: '',
							imageUrl: subscription.plan.imageUrl,
							selected: true
						}
					];

					fetchData.find((v: any) => v.planCode === subscription.plan.planCode).selected = true;
					const mergePreSelectedPlan: any = [...preSelectedPlan, ...fetchData];

					const found = fetchData.find((obj: any) => {
						return obj.selected === true;
					});

					const modifiedData = found ? fetchData : mergePreSelectedPlan;
					setPlansList(modifiedData);
				} else {
					dispatch(
						openNotificationError({
							type: 'ERROR',
							heading: 'Error',
							body: notification.message.timeOutError
						})
					);

					setIsSubscriptionSkeleton(false);
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSubscriptionSkeleton(false);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
		setSubscriptionDetail(subscription);
		window.lastFocusElement = event.target.id;
	};

	const handleUpdateSubscriptionAppBundleModal = (
		event: any,
		subscription: any,
		updateType: any
	) => {
		event.preventDefault();

		if (updateType === 'Renew') {
			setIsRenewAppBundleModalOpen(true);
		} else {
			setIsUpdateAppBundleModalOpen(true);
		}

		setIsSubscriptionSkeleton(true);

		requestRedwolfService(subscriptionAPI.getPlanUpgrades(token, subscription.plan.planCode))
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success') {
					setIsSubscriptionSkeleton(false);
					const fetchData = response.data.toArray();

					const preSelectedPlan = [
						{
							planCode: subscription.plan.planCode,
							appName: subscription.plan.appName,
							name: subscription.plan.name,
							price: subscription.plan.price,
							billingInterval: subscription.plan.billingInterval,
							billingDate: '',
							imageUrl: subscription.plan.imageUrl,
							selected: true
						}
					];

					fetchData.find((v: any) => v.planCode === subscription.plan.planCode).selected = true;
					const mergePreSelectedPlan: any = [...preSelectedPlan, ...fetchData];

					const found = fetchData.find((obj: any) => {
						return obj.selected === true;
					});

					const modifiedData = found ? fetchData : mergePreSelectedPlan;
					setPlansList(modifiedData);
				} else {
					dispatch(
						openNotificationError({
							type: 'ERROR',
							heading: 'Error',
							body: notification.message.timeOutError
						})
					);

					setIsSubscriptionSkeleton(false);
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSubscriptionSkeleton(false);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
		setSubscriptionDetail(subscription);
		window.lastFocusElement = event.target.id;
	};

	const updateSubscriptionModalToggle = (
		isUpdateSubscriptionModalOpen: any,
		emptyPlansList: any,
		reRenderSubscriptionList: any
	) => {
		setIsUpdateModalOpen(isUpdateSubscriptionModalOpen);

		if (emptyPlansList) {
			setPlansList([]);
		}
		if (reRenderSubscriptionList) {
			getSubscriptionList();
		}
	};

	const updateAppBundleSubscriptionModalToggle = (
		isAppBundleUpdateSubscriptionModalOpen: any,
		emptyPlansList: any,
		reRenderSubscriptionList: any
	) => {
		setIsUpdateAppBundleModalOpen(isAppBundleUpdateSubscriptionModalOpen);

		if (emptyPlansList) {
			setPlansList([]);
		}
		if (reRenderSubscriptionList) {
			getSubscriptionList();
		}
	};

	const renewSubscriptionModalToggle = (
		isRenewSubscriptionModalOpen: any,
		emptyPlansList: any,
		reRenderSubscriptionList: any
	) => {
		setIsRenewModalOpen(isRenewSubscriptionModalOpen);

		if (emptyPlansList) {
			setPlansList([]);
		}
		if (reRenderSubscriptionList) {
			getSubscriptionList();
		}
	};

	const renewAppBundleSubscriptionModalToggle = (
		isRenewAppBundleSubscriptionModalOpen: any,
		emptyPlansList: any,
		reRenderSubscriptionList: any
	) => {
		setIsRenewAppBundleModalOpen(isRenewAppBundleSubscriptionModalOpen);

		if (emptyPlansList) {
			setPlansList([]);
		}
		if (reRenderSubscriptionList) {
			getSubscriptionList();
		}
	};

	const handlePagination = (currentPage: any) => {
		setCurrentPageNumber(parseInt(currentPage));
	};

	const paginationPerPage = 5;
	const indexOfLastItem = currentPageNumber * paginationPerPage;
	const indexOfFirstItem = indexOfLastItem - paginationPerPage;

	const currentSubscriptionsList = subscriptionsList.slice(indexOfFirstItem, indexOfLastItem);

	return (
		<>
			<div
				className="sb-pr-component subscrip_comp"
				ref={subscriptionContainerFocusRef}
				{...subscriptionTabindex}>
				<div className="subs-head">
					<h2 id="subs-drop-title" className="ac-title">
						Subscriptions
					</h2>

					{currentSubscriptionsList.length > 0 && (
						<div className="subs-dropdown" id="empty-subscription-dropdown">
							<div className="select-field">
								<Select
									defaultValue={subscriptionDropDownOptions[0]}
									isSearchable={false}
									styles={reactSelectStyles}
									onMenuOpen={onDropdownMenuOpen}
									onMenuClose={onDropdownMenuClose}
									menuIsOpen={isDropdownOpen}
									onKeyDown={onDropdownKeyDown}
									options={subscriptionDropDownOptions}
									onChange={handleSorting}
									aria-labelledby="subs-drop-title"
									components={{ IndicatorSeparator: () => null, DropdownIndicator }}
								/>
							</div>
						</div>
					)}
				</div>

				{isSkeletonLoading ? (
					<TableSkeleton />
				) : (
					currentSubscriptionsList.length === 0 && (
						<p className="empty-title">You do not have any subscriptions.</p>
					)
				)}

				{currentSubscriptionsList.length > 0 && (
					<div className="subs-table">
						<table className="table subscrip-table">
							<thead>
								<tr>
									<th scope="col">App</th>
									<th scope="col">Status</th>
									<th scope="colgroup" colSpan={2}>
										Billing Date
									</th>
								</tr>
							</thead>

							<tbody id="subscription-append">
								{currentSubscriptionsList &&
									currentSubscriptionsList.map((subscription: any, index: number) => {
										const pendingChangeAddOns = subscription?.pendingChange?.addOns
											? subscription?.pendingChange?.addOns.toArray()
											: [];

										let pendingChangeTotalAddonPrice: any = 0.0;

										if (pendingChangeAddOns.length > 0) {
											pendingChangeAddOns.map(function (item: any) {
												pendingChangeTotalAddonPrice += parseFloat(item.price.substring(1));
												return pendingChangeTotalAddonPrice;
											});
										}

										const pendingChangeGrandTotalPrice =
											parseFloat(subscription?.pendingChange?.plan?.price.substring(1)) +
											pendingChangeTotalAddonPrice;

										return (
											<Fragment key={index}>
												<tr className="subs-level2-parent">
													<td className="app-td">
														<div className="icon-title">
															<div className="sapp-icon">
																<img src={subscription.plan.imageUrl} alt="subscriptionImage" />
															</div>

															<div className="sapp-title">
																<h3 id={`subscriptionappname-${index}`}>
																	{subscription.plan.appName}
																</h3>
																<p>
																	{`${subscription?.plan?.name}: ${subscription?.total}/${subscription?.plan?.billingInterval}`}
																</p>
																{subscription.discount ? (
																	<p className="sub-discount">Discount: {subscription.discount}</p>
																) : (
																	subscription.plan.trialLength && (
																		<p>{subscription.plan.trialLength} trial: Free</p>
																	)
																)}
																{subscription.addOns &&
																	subscription.addOns.toArray().length > 0 && (
																		<p>+ {subscription.addOns.toArray().length} Add-Ons</p>
																	)}
																<div className="mob sapp-act-date">
																	<span className="active-state">{subscription.status}</span>
																	<span className="date">{subscription.billingDate}</span>
																</div>
																{subscription.plan.isBundle && (
																	<div className="bundled-list">
																		<div className="include-app-title">Included Apps:</div>
																		<ul className="include-app">
																			{subscription.plan.bundleApps?.toArray() &&
																				subscription.plan.bundleApps
																					.toArray()
																					.map((bundledApp: any, index: any) => {
																						return (
																							<li className="apps-img" key={index}>
																								<img
																									src={bundledApp.imageUrl}
																									alt={bundledApp.appName}
																								/>
																							</li>
																						);
																					})}
																		</ul>
																	</div>
																)}
															</div>
														</div>
													</td>

													<td className="app-td desk">
														<span className="active-state">{subscription.status}</span>
													</td>

													<td className="app-td desk">
														<span className="date">{subscription.billingDate}</span>
													</td>
													{subscription.plan.isBundle === false && (
														<td className="app-td">
															<span className="update-link">
																<a
																	href="/"
																	role="button"
																	className="link"
																	id={`update-renew-${index}`}
																	onClick={(event) =>
																		handleUpdateSubscriptionModal(
																			event,
																			subscription,
																			subscription.status === 'Canceled' ? 'Renew' : 'Update'
																		)
																	}
																	aria-describedby={`subscriptionappname-${index}`}>
																	{subscription.status === 'Canceled' ? 'Renew' : 'Update'}
																</a>
															</span>
														</td>
													)}
													{subscription.plan.isBundle && (
														<td className="app-td">
															<span className="update-link">
																<a
																	href="/"
																	role="button"
																	className="link"
																	id={`cancel-renew-${index}`}
																	onClick={(event) =>
																		handleUpdateSubscriptionAppBundleModal(
																			event,
																			subscription,
																			subscription.status === 'Canceled' ? 'Renew' : 'Cancel'
																		)
																	}
																	aria-describedby={`subscriptionappname-${index}`}>
																	{subscription.status === 'Canceled' ? 'Renew' : 'Cancel'}
																</a>
															</span>
														</td>
													)}
												</tr>

												{subscription.pendingChange && (
													<tr className="subs-row-level2">
														<td className="app-td">
															<div className="icon-title">
																<div className="sapp-icon">
																	<img
																		src={subscription.pendingChange.plan.imageUrl}
																		alt="subscriptionImage"
																	/>
																</div>

																<div className="sapp-title">
																	<h3 id={`subscriptionappname-${index}`}>
																		{subscription.pendingChange.plan.appName}
																	</h3>
																	<p>
																		{`${
																			subscription?.pendingChange?.plan?.name
																		}: $${pendingChangeGrandTotalPrice.toFixed(2)}/${
																			subscription?.pendingChange?.plan?.billingInterval
																		}`}{' '}
																	</p>
																	{pendingChangeAddOns && pendingChangeAddOns.length > 0 && (
																		<p>+ {pendingChangeAddOns.length} Add-Ons</p>
																	)}
																</div>
															</div>
														</td>

														<td className="app-td desk">
															<span className="active-state">Confirmed</span>
														</td>

														<td className="app-td desk">
															<span className="date">
																Starts {subscription.pendingChange.activeDate}
															</span>
														</td>

														<td className="app-td"></td>
													</tr>
												)}
											</Fragment>
										);
									})}
							</tbody>
						</table>

						{isUpdateModalOpen && (
							<UpdateSubscription
								isUpdateModalOpen={isUpdateModalOpen}
								isSkeletonLoading={isSubscriptionSkeleton}
								plansList={plansList}
								subscriptionDetail={subscriptionDetail}
								updateSubscriptionModalToggle={(
									isUpdateSubscriptionModalOpen: boolean,
									emptyPlansList: any,
									reRenderSubscriptionList: any
								) =>
									updateSubscriptionModalToggle(
										isUpdateSubscriptionModalOpen,
										emptyPlansList,
										reRenderSubscriptionList
									)
								}
								updatePurchaseHistory={handleUpdatePurchaseHistory}
							/>
						)}

						{isRenewModalOpen && (
							<RenewSubscription
								isRenewModalOpen={isRenewModalOpen}
								isSkeletonLoading={isSubscriptionSkeleton}
								plansList={plansList}
								subscriptionDetail={subscriptionDetail}
								renewSubscriptionModalToggle={(
									isRenewSubscriptionModalOpen: boolean,
									emptyPlansList: any,
									reRenderSubscriptionList: any
								) =>
									renewSubscriptionModalToggle(
										isRenewSubscriptionModalOpen,
										emptyPlansList,
										reRenderSubscriptionList
									)
								}
								updatePurchaseHistory={handleUpdatePurchaseHistory}
							/>
						)}

						{isUpdateAppBundleModalOpen && (
							<AppBundleUpdateSubscription
								isAppBundleUpdateModalOpen={isUpdateAppBundleModalOpen}
								isSkeletonLoading={isSubscriptionSkeleton}
								plansList={plansList}
								subscriptionDetail={subscriptionDetail}
								updateAppBundleSubscriptionModalToggle={(
									isAppBundleUpdateSubscriptionModalOpen: boolean,
									emptyPlansList: any,
									reRenderSubscriptionList: any
								) =>
									updateAppBundleSubscriptionModalToggle(
										isAppBundleUpdateSubscriptionModalOpen,
										emptyPlansList,
										reRenderSubscriptionList
									)
								}
								updatePurchaseHistory={handleUpdatePurchaseHistory}
							/>
						)}

						{isRenewAppBundleModalOpen && (
							<AppBundleRenewSubscription
								isRenewAppBundleModalOpen={isRenewAppBundleModalOpen}
								isSkeletonLoading={isSubscriptionSkeleton}
								plansList={plansList}
								subscriptionDetail={subscriptionDetail}
								renewAppBundleSubscriptionModalToggle={(
									isRenewAppBundleSubscriptionModalOpen: boolean,
									emptyPlansList: any,
									reRenderSubscriptionList: any
								) =>
									renewAppBundleSubscriptionModalToggle(
										isRenewAppBundleSubscriptionModalOpen,
										emptyPlansList,
										reRenderSubscriptionList
									)
								}
								updatePurchaseHistory={handleUpdatePurchaseHistory}
							/>
						)}

						{subscriptionsList.length > paginationPerPage && (
							<div className="table-navigation">
								<Pagination
									totalNumberOfPages={subscriptionsList.length}
									currentPage={currentPageNumber}
									paginationPerPage={paginationPerPage}
									onPaginationChange={handlePagination}
								/>
							</div>
						)}
					</div>
				)}
			</div>

			<PurchaseHistory updatePurchaseHistory={updatePurchaseHistory} />
		</>
	);
};

export default Subscriptions;
